import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/admin/api";
import { KpiCard, StatusBadge, money, moneyMap } from "@/admin/ui";
import { toast } from "sonner";

const FILTERS = [
  { key: "", label: "All" }, { key: "due_today", label: "Due today" }, { key: "due_week", label: "Due this week" },
  { key: "due_month", label: "Due this month" }, { key: "overdue", label: "Overdue" },
  { key: "partial", label: "Partially paid" }, { key: "paid", label: "Paid" },
];

export default function Payments() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/admin/payments", { params: { filter: filter || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filter]);

  if (!data) return <div className="text-mutedink">Loading payments…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-700 text-navy">Payments</h1>
        <p className="text-sm text-mutedink">Milestones and balances across all projects. Totals are kept per currency.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <KpiCard label="Total due" value={moneyMap(data.totals.due)} tone="amber" />
        <KpiCard label="Total overdue" value={moneyMap(data.totals.overdue)} tone="red" />
        <KpiCard label="Received this month" value={moneyMap(data.totals.received_this_month)} tone="green" />
        <KpiCard label="Total outstanding" value={moneyMap(data.totals.outstanding)} />
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`pay-filter-${f.key || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-sm ${filter === f.key ? "bg-navy text-white" : "bg-white text-navy border border-navy/15"}`}>{f.label}</button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-xs uppercase tracking-wide text-mutedink">
              <tr><th className="px-4 py-3">Customer / Project</th><th className="px-4 py-3">Phase / Description</th>
                <th className="px-4 py-3">Due date</th><th className="px-4 py-3 text-right">Amount due</th>
                <th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {data.rows.map((r, i) => (
                <tr key={i} onClick={() => navigate(`/admin/projects/${r.project_id}`)} className="cursor-pointer hover:bg-cream/60">
                  <td className="px-4 py-3"><div className="font-600 text-navy">{r.project}</div><div className="text-xs text-mutedink">{r.customer}</div></td>
                  <td className="px-4 py-3 text-mutedink">{r.description}{r.phase ? ` · ${r.phase}` : ""}</td>
                  <td className="px-4 py-3 text-mutedink">{r.due_date || "—"}</td>
                  <td className="px-4 py-3 text-right">{money(r.currency, r.amount_due)}</td>
                  <td className="px-4 py-3 text-right text-green-600">{money(r.currency, r.amount_paid)}</td>
                  <td className="px-4 py-3 text-right font-600 text-navy">{money(r.currency, r.outstanding)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-mutedink">No payment milestones.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
